<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { whatsappTextToHtml } from 'dashboard/helper/whatsappTemplateFormatter';

const props = defineProps({
  headerText: { type: String, default: '' },
  headerMediaType: { type: String, default: '' },
  bodyText: { type: String, default: '' },
  footerText: { type: String, default: '' },
  buttons: { type: Array, default: () => [] },
  emptyText: { type: String, default: '' },
  contactName: { type: String, default: '' },
});

const { t } = useI18n();

const bodyHtml = computed(() => whatsappTextToHtml(props.bodyText));
const headerHtml = computed(() => whatsappTextToHtml(props.headerText));
const hasContent = computed(
  () =>
    props.headerText ||
    props.headerMediaType ||
    props.bodyText ||
    props.footerText
);

const currentTime = new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit',
});
</script>

<template>
  <!-- As cores são as do próprio WhatsApp e não mudam com o tema do painel -->
  <div
    class="mx-auto w-full max-w-[300px] rounded-[2.25rem] border-[6px] border-[#1f2933] bg-[#1f2933] shadow-lg"
  >
    <div class="overflow-hidden rounded-[1.75rem] bg-[#efeae2]">
      <div
        class="flex items-center justify-between bg-[#008069] px-4 pb-1 pt-2 text-[10px] font-medium text-white"
      >
        <span>{{ currentTime }}</span>
        <span class="h-3 w-16 rounded-full bg-black/40" />
        <span class="i-lucide-battery-full size-3" />
      </div>

      <div class="flex items-center gap-2 bg-[#008069] px-3 pb-2 text-white">
        <span class="i-lucide-arrow-left size-4" />
        <span
          class="flex size-7 items-center justify-center rounded-full bg-white/25"
        >
          <span class="i-lucide-user size-4" />
        </span>
        <div class="flex min-w-0 flex-col leading-tight">
          <span class="truncate text-xs font-medium">
            {{ contactName || 'WhatsApp' }}
          </span>
          <span class="text-[10px] text-white/80">
            {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.PREVIEW_ONLINE') }}
          </span>
        </div>
      </div>

      <div class="flex min-h-[280px] flex-col gap-1 px-2.5 py-3">
        <div
          v-if="hasContent"
          class="max-w-[92%] self-start rounded-lg rounded-tl-none bg-white px-2 py-1.5 text-[13px] leading-snug text-[#111b21] shadow-sm"
        >
          <div
            v-if="headerMediaType"
            class="mb-1.5 flex h-24 items-center justify-center rounded-md bg-[#d9dde0] text-[11px] font-medium uppercase text-[#54656f]"
          >
            {{ headerMediaType }}
          </div>
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-if="headerText"
            class="mb-1 break-words font-semibold"
            v-html="headerHtml"
          />
          <p v-if="bodyText" class="break-words" v-html="bodyHtml" />
          <!-- eslint-enable vue/no-v-html -->
          <p
            v-if="footerText"
            class="mt-1 break-words text-[11px] text-[#667781]"
          >
            {{ footerText }}
          </p>
          <p class="mt-0.5 text-right text-[10px] text-[#667781]">
            {{ currentTime }}
          </p>
        </div>
        <p v-else class="px-2 text-center text-xs text-[#667781]">
          {{ emptyText }}
        </p>

        <div
          v-for="(buttonText, index) in buttons"
          :key="index"
          class="w-[92%] self-start"
        >
          <span
            class="block rounded-lg bg-white px-2 py-1.5 text-center text-[13px] font-medium text-[#027eb5] shadow-sm"
          >
            {{ buttonText }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
